/**
 * TokenBudgetBanner.jsx
 *
 * Slim banner shown above the Dashboard when the caller's client is approaching
 * or has exceeded its monthly LLM token budget. Renders nothing when the
 * client has no budget set or usage is below 80%.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, ZapOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

export default function TokenBudgetBanner({ isAdmin, activeClientId }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const url = isAdmin && activeClientId
      ? `/usage/monthly?client_id=${encodeURIComponent(activeClientId)}`
      : '/usage/monthly';
    api
      .get(url, { _silent: true })
      .then((res) => { if (!cancelled) setUsage(res.data); })
      .catch(() => { /* silent — banner just won't appear */ });
    return () => { cancelled = true; };
  }, [isAdmin, activeClientId]);

  if (!usage || usage.status === 'ok' || usage.status === 'no_budget') return null;

  const ratio = usage.ratio || 0;
  const pct = Math.min(999, Math.round(ratio * 100));
  const exceeded = usage.status === 'exceeded';

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
        exceeded
          ? 'bg-negative-soft  border-negative-border  text-negative-soft-foreground '
          : 'bg-warning-soft  border-warning-border  text-warning-soft-foreground '
      }`}
      data-testid={`token-budget-banner-${usage.status}`}
    >
      {exceeded ? <ZapOff className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">
          {exceeded
            ? 'Monthly token budget exceeded'
            : `Approaching monthly token budget — ${pct}% used`}
        </p>
        <p className="text-xs opacity-90 mt-0.5">
          {usage.total_tokens.toLocaleString()} of {(usage.monthly_token_budget || 0).toLocaleString()} tokens used this month.
          {exceeded ? ' New simulation runs are temporarily blocked.' : ' New runs will be blocked at 100%.'}
          {isAdmin && (
            <>
              {' '}
              <Link to="/admin?tab=clients" className="underline font-medium">
                Adjust the client budget
              </Link>
              .
            </>
          )}
        </p>
      </div>
      <div className="text-right text-xs font-mono tabular-nums hidden sm:block">
        {pct}%
      </div>
    </div>
  );
}
